import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash, Kanban, Barcode } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { SectionHeader, ConfirmDialog, useFocusHighlight } from "../components/shared";
import { CsvTools } from "../components/CsvTools";
import { ProjectScanner } from "./ProjectScanner";
import { Button } from "../components/ui/button";
import { usePerms } from "../lib/perms";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { DataTable } from "../components/DataTable";

export const PROJECT_STATUSES = ["Not Started", "In Progress", "Completed", "On Hold"];
export const PROJ_STATUS_STYLE = {
  "Not Started": "bg-zinc-100 text-zinc-600 border-zinc-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "On Hold": "bg-amber-50 text-amber-700 border-amber-200",
};
export function ProjectStatusChip({ status }) {
  return <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border rounded-sm ${PROJ_STATUS_STYLE[status] || PROJ_STATUS_STYLE["Not Started"]}`}>{status}</span>;
}

export default function Projects() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { rowProps } = useFocusHighlight();
  const { can } = usePerms();
  const [open, setOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [delProj, setDelProj] = useState(null);
  const EMPTY = { name: "", description: "", customer_id: "", manager: "", status: "Not Started", start_date: "", end_date: "", budget: 0 };
  const [form, setForm] = useState(EMPTY);

  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: salespeople = [] } = useQuery({ queryKey: ["salespeople"], queryFn: async () => (await api.get("/salespeople")).data });

  const create = useMutation({
    mutationFn: async () => (await api.post("/projects", { ...form, budget: Number(form.budget) })).data,
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ["projects"] }); setOpen(false); setForm(EMPTY); toast.success(`Project ${p.code} created`); navigate(`/projects/${p.id}`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/projects/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); toast.success("Project deleted"); },
  });

  return (
    <div className="animate-fade-up">
      <SectionHeader title="Projects" subtitle="Jobs & tracking">
        <div className="flex items-center gap-2 flex-wrap">
          <CsvTools
            entity="projects"
            queryKey={["projects"]}
            label="projects"
            rows={projects}
            columns={[
              { key: "code", label: "code", importKey: false },
              { key: "name", label: "name" },
              { key: "description", label: "description" },
              { key: "manager", label: "manager" },
              { key: "status", label: "status" },
              { key: "start_date", label: "start_date" },
              { key: "end_date", label: "end_date" },
              { key: "budget", label: "budget" },
              { key: "customer_name", label: "customer_name", importKey: false },
            ]}
          />
          <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="scan-project-button" className="rounded-sm h-9"><Barcode size={16} weight="bold" className="mr-1" /> Scan Barcode</Button>
          {can("projects.edit") && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-project-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9"><Plus size={16} weight="bold" className="mr-1" /> New Project</Button>}
        </div>
      </SectionHeader>

      <DataTable
        testid="projects-table"
        data={projects}
        rowKey={(p) => p.id}
        rowTestid="project-row"
        rowProps={(p) => rowProps(p.id)}
        empty="No projects yet."
        columns={[
          { key: "code", header: "Code", filter: "text", cellClassName: "font-mono text-xs font-medium", cell: (p) => <button onClick={() => navigate(`/projects/${p.id}`)} data-testid="open-project" className="text-primary hover:underline">{p.code}</button> },
          { key: "name", header: "Name", filter: "text", cellClassName: "text-sm font-medium" },
          { key: "customer_name", header: "Customer", filter: "text", cellClassName: "text-sm text-muted-foreground", cell: (p) => p.customer_name || "—" },
          { key: "manager", header: "Manager", filter: "text", cellClassName: "text-sm text-muted-foreground", cell: (p) => p.manager || "—" },
          { key: "status", header: "Status", filter: "select", options: PROJECT_STATUSES, cell: (p) => <ProjectStatusChip status={p.status} /> },
          { key: "budget", header: "Budget", align: "right", numeric: true, cellClassName: "tabular text-sm", cell: (p) => money(p.budget) },
          { key: "budget_cost", header: "Budget Cost", align: "right", numeric: true, cellClassName: "tabular text-sm", cell: (p) => money(p.budget_cost || 0) },
          { key: "ledger_cost", header: "Ledger Cost", align: "right", numeric: true, cellClassName: "tabular text-sm", cell: (p) => <span className={p.budget_cost > 0 && p.ledger_cost > p.budget_cost ? "text-destructive font-semibold" : ""} data-testid="project-ledger-cost">{money(p.ledger_cost || 0)}</span> },
          { key: "variance", header: "Variance", align: "right", numeric: true, accessor: (p) => (p.budget_cost || 0) - (p.ledger_cost || 0), cellClassName: "tabular text-sm", cell: (p) => { const v = (p.budget_cost || 0) - (p.ledger_cost || 0); return <span className={v < 0 ? "text-destructive font-semibold" : "text-muted-foreground"} data-testid="project-variance">{money(v)}</span>; } },
          { key: "actions", header: "", sortable: false, align: "right", cell: (p) => can("projects.delete") ? <button onClick={() => setDelProj(p)} data-testid="delete-project" className="p-1.5 text-muted-foreground hover:text-destructive"><Trash size={16} /></button> : null },
        ]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Kanban size={20} className="text-primary" /> New Project</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label className="overline-label">Name</Label><Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div className="col-span-2"><Label className="overline-label">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div>
              <Label className="overline-label">Customer</Label>
              <Select value={form.customer_id || "none"} onValueChange={(v) => setForm({ ...form, customer_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="project-customer"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline-label">Manager</Label>
              <Select value={form.manager || "none"} onValueChange={(v) => setForm({ ...form, manager: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="project-manager"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{salespeople.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline-label">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="project-status"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="overline-label">Budget</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-1 rounded-sm num" /></div>
            <div><Label className="overline-label">Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label className="overline-label">End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!form.name || create.isPending} data-testid="save-project-button" className="rounded-sm bg-primary hover:bg-primary/90">Create Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delProj} onOpenChange={(v) => { if (!v) setDelProj(null); }} title="Delete this project?" description={delProj ? `${delProj.code} · ${delProj.name} will be permanently deleted.` : ""} confirmLabel="Delete" testid="project-delete-confirm" onConfirm={() => { if (delProj) del.mutate(delProj.id); setDelProj(null); }} />

      <ProjectScanner open={scanOpen} onOpenChange={setScanOpen} projects={projects} onOpenProject={(id) => navigate(`/projects/${id}`)} />
    </div>
  );
}
